import { HttpClient, HttpParams } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { NotificationActions } from '@ninety/platform/state';

import { CalendarEvent, CalendarEventsService } from './calendar-events.model';

@Injectable({ providedIn: 'root' })
export class OutlookCalendarService implements CalendarEventsService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly baseUrl = `/api/v4/integrations/microsoft-calendar`;

  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private readonly _error = signal<string | null>(null);
  readonly error = this._error.asReadonly();

  private readonly _events = signal<CalendarEvent[]>([]);
  readonly events = this._events.asReadonly();

  // This service is a shared singleton (providedIn: 'root'), but multiple independent UI
  // surfaces call getEvents() for different connections/date-ranges (e.g. the event picker and a
  // background lookup on a host dialog). Without this guard, a slower, older request's response
  // could land after a newer one and clobber `_events` with stale data.
  private latestRequestId = 0;

  getEvents(integrationUserConnectionId: string, startDatetime?: string, endDatetime?: string): void {
    if (!integrationUserConnectionId) {
      this._error.set('Integration ID is required to fetch events');
      this.store.dispatch(NotificationActions.showErrorToast({ message: this.error()! }));
      return;
    }
    const requestId = ++this.latestRequestId;
    this._loading.set(true);
    this._error.set(null);
    let params = new HttpParams();
    if (startDatetime) params = params.set('startDatetime', startDatetime);
    if (endDatetime) params = params.set('endDatetime', endDatetime);
    this.http
      .get<CalendarEvent[]>(`${this.baseUrl}/${integrationUserConnectionId}/events`, { params })
      .pipe(
        tap({
          next: events => {
            if (requestId === this.latestRequestId) {
              this._events.set(events);
            }
          },
          error: () => {
            if (requestId !== this.latestRequestId) {
              return;
            }
            const message = 'Failed to load Outlook Calendar events';
            this._error.set(message);
            this.store.dispatch(NotificationActions.showErrorToast({ message }));
          },
        }),
        catchError(() => EMPTY),
        finalize(() => {
          if (requestId === this.latestRequestId) {
            this._loading.set(false);
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }
}
